import axios from "axios";
import { API_URL } from "../config";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  ArrowLeft,
  FileText,
  ShieldCheck,
  User,
  Calendar,
  Clock,
  CheckCircle2,
  Check,
  X,
  MessageSquare,
  Eye,
  Download,
  Send,
  XCircle,
  Hourglass,
  Filter,
  ExternalLink,
} from "lucide-react";

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

const TABS = ["Request Details", "Document Preview", "Verification Details", "Audit Trail"];
const POLL_MS = 20000;
const SIGN_WIZARD_ROUTE = "/app"; // route that renders App.js (Select Document step)

const AUDIT_LABELS = {
  created: "Request Submitted",
  notified: "Recipient Notified",
  signed: "Signed",
  approved: "Approved",
  rejected: "Rejected",
  info_requested: "More Information Requested",
  reminder: "Reminder Sent",
  cancelled: "Request Cancelled",
  completed: "Request Completed",
  expired: "Request Expired",
};

// icon + colour per audit action, reusing icons already imported elsewhere on this page
const AUDIT_ICONS = {
  created: { Icon: FileText, tone: "bg-blue-50 text-blue-600" },
  notified: { Icon: Send, tone: "bg-indigo-50 text-indigo-600" },
  signed: { Icon: Check, tone: "bg-green-50 text-green-600" },
  approved: { Icon: Check, tone: "bg-green-50 text-green-600" },
  rejected: { Icon: X, tone: "bg-red-50 text-red-500" },
  info_requested: { Icon: MessageSquare, tone: "bg-purple-50 text-purple-600" },
  reminder: { Icon: Send, tone: "bg-amber-50 text-amber-600" },
  cancelled: { Icon: XCircle, tone: "bg-gray-100 text-gray-500" },
  completed: { Icon: CheckCircle2, tone: "bg-green-50 text-green-600" },
  expired: { Icon: Clock, tone: "bg-gray-100 text-gray-500" },
};

const formatDate = (v) => {
  const d = new Date(v);
  if (!v || isNaN(d.getTime())) return "—";
  return (
    d.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" }) +
    " " +
    d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false })
  );
};

const show = (v) => (v === undefined || v === null || v === "" ? "—" : v);
const errMsg = (err, fallback) => {
  const data = err?.response?.data;
  if (data?.error) return data.error;
  const base = fallback.replace(/\.$/, "");
  const status = err?.response?.status;
  return status
    ? `${base} (the server returned ${status}).`
    : `${base} (could not reach the server).`;
};

const statusPillClass = (status = "") => {
  if (status === "Completed" || status === "Document Signed" || status.endsWith("by you")) return "bg-green-50 text-green-600";
  if (status === "Rejected") return "bg-red-50 text-red-500";
  if (status === "Cancelled" || status === "Expired") return "bg-gray-100 text-gray-500";
  if (status.startsWith("Pending")) return "bg-amber-50 text-amber-600";
  return "bg-blue-50 text-blue-600";
};

const stepStatusLabel = (r) =>
  ({
    waiting: "Waiting",
    pending: "Pending",
    completed: r.role === "Signer" ? "Signed" : "Approved",
    rejected: "Rejected",
    cancelled: "Cancelled",
  }[r.status] || r.status);

const stepStatusClass = (s) =>
  ({
    waiting: "bg-gray-100 text-gray-500",
    pending: "bg-amber-50 text-amber-600",
    completed: "bg-green-50 text-green-600",
    rejected: "bg-red-50 text-red-500",
    cancelled: "bg-gray-100 text-gray-400",
  }[s] || "bg-gray-100 text-gray-500");

const BANNER_TONES = {
  indigo: { box: "bg-indigo-50 border-indigo-100", icon: "bg-indigo-100 text-indigo-600", Icon: Clock },
  blue: { box: "bg-blue-50 border-blue-100", icon: "bg-blue-100 text-blue-600", Icon: Hourglass },
  green: { box: "bg-green-50 border-green-100", icon: "bg-green-100 text-green-600", Icon: CheckCircle2 },
  red: { box: "bg-red-50 border-red-100", icon: "bg-red-100 text-red-500", Icon: XCircle },
  gray: { box: "bg-gray-50 border-gray-200", icon: "bg-gray-200 text-gray-500", Icon: XCircle },
};

// Valid / Invalid / Pending ("Pending" here also covers "not implemented yet")
const STATE_TONE = {
  Valid: { dot: "bg-green-500", Icon: CheckCircle2, badge: "bg-green-50 text-green-600" },
  Invalid: { dot: "bg-red-500", Icon: XCircle, badge: "bg-red-50 text-red-500" },
  Pending: { dot: "bg-gray-300", Icon: Hourglass, badge: "bg-gray-100 text-gray-500" },
};

function toCSV(rows) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const header = ["#", "Timestamp", "Action", "Details", "User", "Source", "Status"];
  const lines = [header.join(",")];
  rows.forEach((r, i) => {
    lines.push(
      [i + 1, formatDate(r.timestamp), AUDIT_LABELS[r.action] || r.action, r.detail || "", r.actor, r.source, "Success"]
        .map(esc)
        .join(",")
    );
  });
  return lines.join("\n");
}

function downloadCSV(filename, csv) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const pluralS = (n) => (n === 1 ? "" : "s");

// Gives every item a stable key built from its own data (never its position).
// Items that are identical get a running number so keys stay unique.
const withKeys = (items) => {
  const seen = {};
  return items.map((item) => {
    const base = `${item.timestamp}|${item.action}|${item.actor}`;
    seen[base] = (seen[base] || 0) + 1;
    return { ...item, _key: `${base}|${seen[base]}` };
  });
};

const CIRCLE_CLASSES = {
  completed: "bg-green-500 text-white",
  rejected: "bg-red-500 text-white",
  pending: "bg-indigo-600 text-white ring-4 ring-indigo-100",
};
const CIRCLE_DEFAULT = "bg-gray-200 text-gray-500";

const CHECK_TEXT_COLOR = { Valid: "text-green-600", Invalid: "text-red-500", Pending: "text-gray-400" };
const CHECK_ICON_COLOR = { Valid: "text-green-500", Invalid: "text-red-500" };

const TERMINAL_BANNERS = {
  completed: { tone: "green", title: "Completed", text: "Every step has been finished." },
  rejected: { tone: "red", title: "Rejected", text: "This request was rejected. See the audit trail for the reason." },
  cancelled: { tone: "gray", title: "Cancelled", text: "The sender cancelled this request." },
  expired: { tone: "gray", title: "Expired", text: "This request expired before everyone finished." },
};

const CERT_RESULT = {
  Valid: { title: "Certificate Valid", text: "The certificate is within its validity period." },
  Invalid: { title: "Certificate Expired", text: "The certificate is outside its validity period." },
};
const CERT_UNAVAILABLE = {
  title: "Details Unavailable",
  text: "No readable certificate was attached to this signature.",
};

const validOr = (state, ok, bad) => (state === "Valid" ? ok : bad);

const monoText = (v) => <span className="font-mono text-[11px] break-all">{show(v)}</span>;

const tokenPreview = (ts) => (ts ? String(ts).slice(0, 120) : null);

const waitingText = (req, fallback) => (req.waitingOn ? `Waiting on ${req.waitingOn}.` : fallback);

const buildBanner = (req, verb) => {
  const terminal = TERMINAL_BANNERS[req.requestStatus];
  if (terminal) return terminal;
  if (req.canAct) {
    return { tone: "indigo", title: req.status, text: `It's your turn. Please ${verb.toLowerCase()} this document.` };
  }
  if (req.status === "Document Signed" || req.status.endsWith("by you")) {
    return { tone: "blue", title: req.status, text: waitingText(req, "Your step is done.") };
  }
  return { tone: "blue", title: "In Progress", text: waitingText(req, "Waiting for the remaining steps.") };
};

const buildFileSrc = (req, email, previewVersion) => {
  const original = previewVersion === "original" ? "&version=original" : "";
  // cache-buster after a new signature
  const bust = req.lastSignedAt ? `&v=${encodeURIComponent(req.lastSignedAt)}` : "";
  return `${API_URL}${req.fileUrl}?user_email=${encodeURIComponent(email)}${original}${bust}`;
};

/* ---- verification blocks (one small builder per block) ---- */

const hashBlock = (req, check) => ({
  key: "hash",
  title: "File Integrity Verification",
  description: "The uploaded file's SHA-256 hash is checked against the one recorded when this request was created.",
  state: check.state,
  fields: [
    { label: "Hash Algorithm", value: "SHA-256" },
    { label: "Recorded Hash", value: monoText(req.hash) },
    { label: "Verification Method", value: show(req.verificationMethod) },
    { label: "Status", value: check.state, badge: true },
  ],
  resultTitle: validOr(check.state, "Hash Verified", "Hash Mismatch"),
  resultText: validOr(
    check.state,
    "The file matches the hash recorded when the request was created.",
    "The file on disk no longer matches the recorded hash."
  ),
});

const validityBlock = (req, check) => ({
  key: "validity",
  title: "Request Validity",
  description: "Confirms the request has not passed its expiry date.",
  state: check.state,
  fields: [
    { label: "Expires On", value: formatDate(req.expiresOn) },
    { label: "Status", value: check.state, badge: true },
  ],
  resultTitle: validOr(check.state, "Within Validity Period", "Expired"),
  resultText: validOr(
    check.state,
    "This request is still within its validity window.",
    "This request has passed its expiry date."
  ),
});

const stepsBlock = (req, check) => ({
  key: "steps",
  title: "Signing Sequence",
  description: "Confirms everyone ahead of you in the signing order has already finished their step.",
  state: check.state,
  fields: [
    { label: "Your Step", value: req.myStep ? `${req.myStep} of ${req.totalSteps}` : "—" },
    { label: "Status", value: check.state, badge: true },
  ],
  resultTitle: validOr(check.state, "Your Turn", "Waiting"),
  resultText: validOr(
    check.state,
    "Everyone ahead of you has completed their step.",
    `Waiting on ${req.waitingOn || "earlier recipients"}.`
  ),
});

const signedBlock = (check, latestSig) => ({
  key: "signed",
  title: "Signed Document Integrity",
  description: "The latest signed PDF's SHA-256 hash is checked against the one recorded when it was signed.",
  state: check.state,
  fields: [
    { label: "Signed By", value: show(latestSig?.signer) },
    { label: "Signed On", value: formatDate(latestSig?.signedAt) },
    { label: "Signing Mode", value: show([latestSig?.signMode, latestSig?.algorithm].filter(Boolean).join(" · ")) },
    { label: "Signed File Hash", value: monoText(latestSig?.signedHash) },
    { label: "Status", value: check.state, badge: true },
  ],
  resultTitle: validOr(check.state, "Signed File Verified", "Signed File Modified"),
  resultText: validOr(
    check.state,
    "The signed PDF matches the hash recorded at signing time.",
    "The signed PDF no longer matches the hash recorded at signing time."
  ),
});

const signerLabel = (sg) => {
  const suffix = sg.email ? ` (${sg.email})` : "";
  return `${show(sg.signer)}${suffix}`;
};

const certBlock = (sg, key, count) => {
  const result = CERT_RESULT[sg.certState] || CERT_UNAVAILABLE;
  return {
    key,
    title: count > 1 ? `Signer Certificate — ${sg.signer}` : "Signer Certificate",
    description: "Details of the digital certificate attached to the signature.",
    state: sg.certState,
    fields: [
      { label: "Signer", value: signerLabel(sg) },
      { label: "Subject", value: <span className="break-all">{show(sg.certSubject)}</span> },
      { label: "Issuer", value: <span className="break-all">{show(sg.certIssuer)}</span> },
      { label: "Serial Number", value: monoText(sg.certSerial) },
      { label: "Valid From", value: formatDate(sg.certNotBefore) },
      { label: "Valid Until", value: formatDate(sg.certNotAfter) },
      { label: "SHA-256 Fingerprint", value: monoText(sg.certFingerprint) },
      { label: "Status", value: sg.certState, badge: true },
    ],
    resultTitle: result.title,
    resultText: result.text,
  };
};

const PENDING_CERT_BLOCK = {
  key: "cert-pending",
  title: "Signer Certificate",
  description: "Details of the digital certificate attached to the signature.",
  state: "Pending",
  fields: [
    { label: "Subject", value: "—" },
    { label: "Issuer", value: "—" },
    { label: "Status", value: "Pending", badge: true },
  ],
  resultTitle: "Not Signed Yet",
  resultText: "Certificate details appear here once a Signer completes their step.",
};

const ipfsLink = (url) =>
  url ? (
    <a href={url} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline break-all">
      {url}
    </a>
  ) : (
    "—"
  );

const chainBlock = (req, latestSig) => {
  const anchored = !!latestSig?.ipfsCid;
  return {
    key: "chain",
    title: "Blockchain / IPFS Anchoring",
    description: "Where the signed document was anchored.",
    state: anchored ? "Valid" : "Pending",
    fields: [
      { label: "Blockchain Network", value: show(req.network) },
      { label: "IPFS CID", value: monoText(req.ipfsCid) },
      { label: "IPFS Link", value: ipfsLink(latestSig?.ipfsUrl) },
      { label: "Timestamp Token", value: monoText(tokenPreview(latestSig?.timestamp)) },
    ],
    resultTitle: anchored ? "Anchored" : "Not Yet Available",
    resultText: anchored
      ? "The signed document was uploaded to IPFS."
      : "Nothing has been anchored yet — this appears after a Signer signs.",
  };
};

// Builds the numbered blocks of the Verification Details tab from req.checks.
const buildVerificationBlocks = (req) => {
  const find = (needle) => req.checks.find((c) => c.label.toLowerCase().includes(needle));
  const hashCheck = find("hash");
  const validityCheck = find("validity");
  const stepsCheck = find("previous steps");
  const signedCheck = find("signed file");
  const signatures = req.signatures || [];
  const latestSig = signatures[signatures.length - 1];
  const certs = signatures.length
    ? signatures.map((sg, n) => certBlock(sg, `cert-${sg.certSerial || sg.signedAt || n}`, signatures.length))
    : [PENDING_CERT_BLOCK];

  return [
    hashCheck && hashBlock(req, hashCheck),
    validityCheck && validityBlock(req, validityCheck),
    stepsCheck && stepsBlock(req, stepsCheck),
    signedCheck && signedBlock(signedCheck, latestSig),
    ...certs,
    chainBlock(req, latestSig),
  ].filter(Boolean);
};

const isOverallValid = (blocks) =>
  blocks.filter((b) => b.key !== "chain" && b.key !== "cert-pending").every((b) => b.state === "Valid");

const buildAuditView = (audit, actionFilter, userFilter) => {
  const matches = (a) =>
    (actionFilter === "All Actions" || a.action === actionFilter) &&
    (userFilter === "All Users" || a.actor === userFilter);
  const rows = audit
    .filter(matches)
    .map((a) => ({ ...a, source: a.actor === "system" ? "Backend" : "Web Portal" }));
  return {
    actions: Array.from(new Set(audit.map((a) => a.action))),
    users: Array.from(new Set(audit.map((a) => a.actor))),
    rows: withKeys(rows),
  };
};

/* ------------------------------------------------------------------ */
/* Small shared components                                             */
/* ------------------------------------------------------------------ */

function Section({ title, right, children }) {
  return (
    <div className="border border-gray-200 rounded-xl bg-white p-4">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        {right}
      </div>
      {children}
    </div>
  );
}

function Row({ label, children, labelWidth = "w-[130px]" }) {
  return (
    <div className="flex items-start gap-2 text-xs">
      <span className={`${labelWidth} shrink-0 text-gray-500`}>{label}</span>
      <span className="text-gray-400">:</span>
      <span className="text-gray-800 font-medium min-w-0 break-words">{children}</span>
    </div>
  );
}

function SidebarCard({ title, children }) {
  return (
    <div className="border border-gray-200 rounded-xl bg-white p-3">
      <h3 className="text-xs font-semibold text-gray-900 mb-2">{title}</h3>
      {children}
    </div>
  );
}

function ActionModal({ title, placeholder, confirmLabel, danger, busy, onCancel, onConfirm }) {
  const [text, setText] = useState("");
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-md p-5 shadow-xl">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">{title}</h3>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={4}
          placeholder={placeholder}
          className="w-full border border-gray-200 rounded-lg p-3 text-sm outline-none focus:border-indigo-400"
        />
        <div className="flex justify-end gap-2 mt-4">
          <button
            onClick={onCancel}
            className="h-9 px-4 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            disabled={busy || !text.trim()}
            onClick={() => onConfirm(text.trim())}
            className={`h-9 px-4 rounded-lg text-sm font-medium text-white disabled:opacity-50 ${
              danger ? "bg-red-500 hover:bg-red-600" : "bg-indigo-600 hover:bg-indigo-700"
            }`}
          >
            {busy ? "Sending..." : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Hooks                                                               */
/* ------------------------------------------------------------------ */

function useUserEmail() {
  return useMemo(() => {
    const user = JSON.parse(localStorage.getItem("user")) || {};
    return (user.email || "").toLowerCase();
  }, []);
}

function useRequest(id, email) {
  const [req, setReq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/workflow/requests/${id}`, { params: { user_email: email } });
        setReq(res.data);
        setError("");
      } catch (err) {
        console.error("REQUEST DETAIL ERROR:", err);
        if (!silent) setError(errMsg(err, "Could not load this request."));
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [id, email]
  );

  useEffect(() => {
    load();
    // keep the page current so a waiting recipient sees when it becomes their turn
    const timer = setInterval(() => load(true), POLL_MS);
    return () => clearInterval(timer);
  }, [load]);

  return { req, loading, error, load };
}

function useRequestActions({ id, email, load, navigate }) {
  const [modal, setModal] = useState(null); // which dialog is open: "reject", "info" or none
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(null); // message to show: its type ("success" or "error") and text

  const run = async (call, okText) => {
    setBusy(true);
    setFlash(null);
    try {
      const res = await call();
      setFlash({ type: "success", text: res?.data?.message || okText });
      await load(true);
    } catch (err) {
      setFlash({ type: "error", text: errMsg(err, "Action failed. Please try again.") });
      load(true); // state may have changed under us (e.g. someone else acted)
    } finally {
      setBusy(false);
      setModal(null);
    }
  };

  const post = (path, body = {}) =>
    axios.post(`${API_URL}/api/workflow/requests/${id}/${path}`, { user_email: email, ...body });

  // Signers go through the full signing wizard; the request is only marked
  // signed after the wizard's final "Sign Document" click (see review.js).
  const startSigning = () => {
    navigate(`${SIGN_WIZARD_ROUTE}?workflowRequest=${encodeURIComponent(id)}`);
  };

  const primaryAction = (verb, documentName) => {
    if (verb === "Sign") return startSigning();
    if (window.confirm(`${verb} "${documentName}"? This is logged and cannot be undone.`)) {
      run(() => post("approve"), "Done");
    }
  };

  const remind = () => run(() => post("remind"), "Reminder sent");

  const cancelRequest = () => {
    if (window.confirm("Cancel this request? Nobody will be able to act on it anymore.")) {
      run(() => axios.delete(`${API_URL}/api/workflow/requests/${id}`, { params: { user_email: email } }), "Request cancelled");
    }
  };

  const reject = (reason) => run(() => post("reject", { reason }), "Request rejected");
  const requestInfo = (message) => run(() => post("request-info", { message }), "Question sent");

  return { modal, setModal, busy, flash, primaryAction, remind, cancelRequest, reject, requestInfo };
}

/* ------------------------------------------------------------------ */
/* Components                                                          */
/* ------------------------------------------------------------------ */

function StepMarker({ r, signingOrder }) {
  if (r.status === "completed") return <Check size={14} />;
  if (r.status === "rejected") return <X size={14} />;
  return signingOrder ? <>{r.step}</> : <User size={13} />;
}

// Vertical list of everyone in the signing/approval chain, in order
function SignerChain({ recipients, signingOrder, myEmail }) {
  return (
    <ol>
      {recipients.map((r, i) => {
        const last = i === recipients.length - 1;
        const circle = CIRCLE_CLASSES[r.status] || CIRCLE_DEFAULT;
        return (
          <li key={r.email} className="relative pl-11 pb-5 last:pb-0">
            {!last && <span className="absolute left-[13px] top-7 bottom-0 w-px bg-gray-200" />}
            <span
              className={`absolute left-0 top-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${circle}`}
            >
              <StepMarker r={r} signingOrder={signingOrder} />
            </span>

            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <p className="text-xs font-semibold text-gray-800">
                  {r.name || r.email}
                  {r.email === myEmail && (
                    <span className="ml-2 px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600 text-[10px] font-medium">
                      You
                    </span>
                  )}
                </p>
                <p className="text-[11px] text-gray-400">
                  {r.email} • {r.role}
                </p>
                {r.actedAt && <p className="text-[11px] text-gray-400">{formatDate(r.actedAt)}</p>}
                {r.comment && <p className="text-[11px] text-gray-600 mt-1">“{r.comment}”</p>}
              </div>
              <span className={`px-2.5 py-0.5 rounded-md text-[11px] font-medium ${stepStatusClass(r.status)}`}>
                {stepStatusLabel(r)}
              </span>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

// One numbered verification block: icon + title/description header, a details
// list on the left and a coloured result card on the right — used for both
// checks the backend actually performs and ones it doesn't (yet).
function VerificationBlock({ number, title, description, state, fields, resultTitle, resultText }) {
  const tone = STATE_TONE[state] || STATE_TONE.Pending;
  const ResultIcon = tone.Icon;
  return (
    <div className="border border-gray-200 rounded-xl bg-white p-4">
      <div className="flex items-start gap-3 mb-3">
        <span className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-white ${tone.dot}`}>
          <ResultIcon size={15} />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900">
            {number}. {title}
          </p>
          <p className="text-[11px] text-gray-500 mt-0.5">{description}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_160px] gap-3">
        <div className="space-y-2">
          {fields.map((f) => (
            <Row key={f.label} label={f.label} labelWidth="w-[130px]">
              {f.badge ? (
                <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${tone.badge}`}>{f.value}</span>
              ) : (
                f.value
              )}
            </Row>
          ))}
        </div>
        <div className={`rounded-lg p-3 flex flex-col items-center justify-center text-center gap-1 ${tone.badge}`}>
          <ResultIcon size={20} />
          <p className="text-xs font-semibold">{resultTitle}</p>
          <p className="text-[10px] leading-snug">{resultText}</p>
        </div>
      </div>
    </div>
  );
}

// Chronological event list — used for both the Verification "Timeline" card
// and the Audit Trail "Request Timeline" card in the sidebar.
function EventTimeline({ events }) {
  if (!events.length) {
    return <p className="text-xs text-gray-400 py-4 text-center">No events recorded yet.</p>;
  }
  return (
    <ol className="space-y-3">
      {withKeys(events).map((e) => (
        <li key={e._key} className="flex items-start gap-2.5">
          <span className="mt-0.5 w-5 h-5 rounded-full bg-green-100 text-green-600 flex items-center justify-center shrink-0">
            <Check size={11} />
          </span>
          <div className="min-w-0">
            <p className="text-xs font-medium text-gray-800">{AUDIT_LABELS[e.action] || e.action}</p>
            <p className="text-[11px] text-gray-400">{formatDate(e.timestamp)}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

// Transaction ID / Network / Block Number / IPFS CID / Explorer Link.
// These fields aren't produced by the backend yet, so they read straight off
// `req` and fall back to "—" until the API starts sending them.
function AdditionalInfo({ req }) {
  return (
    <div className="space-y-2.5">
      <Row label="Transaction ID" labelWidth="w-[100px]">
        <span className="font-mono text-[11px] break-all">{show(req.txId)}</span>
      </Row>
      <Row label="Network" labelWidth="w-[100px]">
        {show(req.network)}
      </Row>
      <Row label="Block Number" labelWidth="w-[100px]">
        {show(req.blockNumber)}
      </Row>
      <Row label="IPFS CID" labelWidth="w-[100px]">
        <span className="font-mono text-[11px] break-all">{show(req.ipfsCid)}</span>
      </Row>
      <Row label="Explorer Link" labelWidth="w-[100px]">
        {req.explorerUrl ? (
          <a
            href={req.explorerUrl}
            target="_blank"
            rel="noreferrer"
            className="text-indigo-600 hover:underline inline-flex items-center gap-1"
          >
            View <ExternalLink size={11} />
          </a>
        ) : (
          "—"
        )}
      </Row>
    </div>
  );
}

function FlashMessage({ flash }) {
  if (!flash) return null;
  const tone =
    flash.type === "success"
      ? "bg-green-50 border-green-100 text-green-700"
      : "bg-red-50 border-red-100 text-red-600";
  return <div className={`mb-4 text-xs rounded-lg px-4 py-3 border ${tone}`}>{flash.text}</div>;
}

function PageIntro({ req, verb, onBack }) {
  const text = req.canAct
    ? `Please review the details below, then ${verb.toLowerCase()} or reject the request. Your action is logged for audit and compliance.`
    : "Review the details and progress of this request.";
  return (
    <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
      <p className="text-sm text-gray-600 max-w-xl">{text}</p>
      <button
        onClick={onBack}
        className="h-8 px-3 bg-white border border-indigo-300 text-indigo-600 rounded-lg text-xs font-medium hover:bg-indigo-50 flex items-center gap-2"
      >
        <ArrowLeft size={14} />
        Back to Requests
      </button>
    </div>
  );
}

function HeaderIconBlock({ icon: Icon, label, children }) {
  return (
    <div className="flex items-center gap-2.5">
      <div className="w-7 h-7 rounded-full bg-indigo-50 flex items-center justify-center">
        <Icon size={14} className="text-indigo-600" />
      </div>
      <div>
        <p className="text-[11px] text-gray-500">{label}</p>
        {children}
      </div>
    </div>
  );
}

function HeaderCard({ req, tab, onTab }) {
  return (
    <div className="border border-gray-200 rounded-xl bg-white">
      <div className="flex items-center gap-x-6 gap-y-2 flex-wrap px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center">
            <FileText size={16} className="text-indigo-600" />
          </div>
          <div>
            <p className="text-[11px] text-gray-500">Request ID</p>
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-gray-900">{req.id}</span>
              <span className={`px-2.5 py-0.5 rounded-md text-[11px] font-medium ${statusPillClass(req.status)}`}>
                {req.status}
              </span>
            </div>
          </div>
        </div>

        <HeaderIconBlock icon={ShieldCheck} label="Request Type">
          <p className="text-xs font-semibold text-gray-800">{req.type}</p>
        </HeaderIconBlock>

        <HeaderIconBlock icon={User} label="Requestor">
          <p className="text-xs font-semibold text-gray-800">{show(req.requester)}</p>
          <p className="text-[11px] text-gray-400">{req.requesterEmail}</p>
        </HeaderIconBlock>

        <HeaderIconBlock icon={Calendar} label="Submitted On">
          <p className="text-xs font-semibold text-gray-800">{formatDate(req.submittedOn)}</p>
        </HeaderIconBlock>
      </div>

      <div className="flex items-center gap-5 px-4 border-t border-gray-100 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => onTab(t)}
            className={`py-2 text-xs font-medium border-b-2 whitespace-nowrap transition ${
              tab === t
                ? "border-indigo-600 text-indigo-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {t}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ---------- tab panels (left column) ---------- */

function RequestDetailsPanel({ view }) {
  const { req, email } = view;
  const orderTitle = req.signingOrder ? "Signing Order" : "Recipients";
  const orderHint = req.signingOrder
    ? "Each person is notified only after the person before them has finished."
    : "Everyone was notified at the same time and can act in any order.";
  return (
    <>
      <Section title="1. Request Information">
        <div className="space-y-3">
          <Row label="Request Type">{show(req.type)}</Row>
          <Row label="Document Name">{show(req.document)}</Row>
          <Row label="File Size">{show(req.size)}</Row>
          <Row label="Hash (SHA-256)">{monoText(req.hash)}</Row>
          <Row label="Sent By">
            {show(req.requester)} ({req.requesterEmail})
          </Row>
          <Row label="Message">{show(req.message)}</Row>
          <Row label="Expires On">{formatDate(req.expiresOn)}</Row>
        </div>
      </Section>

      <Section title={`2. ${orderTitle} (${req.doneSteps} of ${req.totalSteps} done)`}>
        <p className="text-[11px] text-gray-500 mb-4">{orderHint}</p>
        <SignerChain recipients={req.recipients} signingOrder={req.signingOrder} myEmail={email} />
      </Section>

      <Section title="3. Signing Configuration">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
          <div>
            <p className="text-[11px] text-gray-500 mb-1">Verification Method</p>
            <p className="font-medium text-gray-800">{show(req.verificationMethod)}</p>
          </div>
          <div>
            <p className="text-[11px] text-gray-500 mb-1">Signing Mode</p>
            <p className="font-medium text-gray-800">{req.signingOrder ? "One after another" : "All at once"}</p>
          </div>
          <div>
            <p className="text-[11px] text-gray-500 mb-1">Total Steps</p>
            <p className="font-medium text-gray-800">{req.totalSteps}</p>
          </div>
        </div>
      </Section>
    </>
  );
}

const PREVIEW_VERSIONS = [
  ["signed", "Signed"],
  ["original", "Original"],
];

function VersionToggle({ previewVersion, onChange }) {
  return (
    <div className="flex items-center gap-1 text-[11px]">
      {PREVIEW_VERSIONS.map(([v, label]) => (
        <button
          key={v}
          onClick={() => onChange(v)}
          className={`px-2.5 py-1 rounded-md border ${
            previewVersion === v
              ? "bg-indigo-50 border-indigo-300 text-indigo-600 font-medium"
              : "border-gray-200 text-gray-500 hover:bg-gray-50"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function PreviewPanel({ view }) {
  const { isSigned, previewVersion, setPreviewVersion, signatures, fileSrc } = view;
  return (
    <Section
      title="Document Preview"
      right={isSigned ? <VersionToggle previewVersion={previewVersion} onChange={setPreviewVersion} /> : null}
    >
      {isSigned && previewVersion === "signed" && (
        <p className="text-[11px] text-green-600 mb-2">
          Showing the signed document ({signatures.length} signature{pluralS(signatures.length)}).
        </p>
      )}
      <iframe
        title="Document preview"
        src={fileSrc}
        className="w-full h-[calc(100vh-300px)] min-h-[340px] border border-gray-200 rounded-lg bg-gray-50"
      />
      <p className="text-[11px] text-gray-400 mt-3">
        Can't see a preview? Some file types (Word, Excel) can't be shown in the browser —{" "}
        <a href={`${fileSrc}&download=1`} className="text-indigo-600 hover:underline">
          download the file
        </a>{" "}
        instead.
      </p>
    </Section>
  );
}

function OverallBadge({ ok }) {
  const tone = ok ? "bg-green-50 text-green-600" : "bg-amber-50 text-amber-600";
  const Icon = ok ? CheckCircle2 : Hourglass;
  return (
    <span className={`px-3 py-1 rounded-full text-[11px] font-medium flex items-center gap-1.5 ${tone}`}>
      <Icon size={13} />
      {ok ? "Verification Passed" : "Verification Pending"}
    </span>
  );
}

function VerificationPanel({ view }) {
  const { verificationBlocks, overallValid } = view;
  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">Verification Details</h2>
          <p className="text-[11px] text-gray-500 mt-0.5">
            Results of the checks run on this document, signature and request.
          </p>
        </div>
        <OverallBadge ok={overallValid} />
      </div>

      {verificationBlocks.map((b, i) => (
        <VerificationBlock key={b.key} number={i + 1} {...b} />
      ))}
    </>
  );
}

const FILTER_SELECT_CLASS =
  "appearance-none border border-gray-200 rounded-lg pl-8 pr-7 h-8 text-xs text-gray-700 bg-white outline-none focus:border-indigo-400 cursor-pointer";

function AuditRow({ a, number }) {
  const ic = AUDIT_ICONS[a.action] || { Icon: FileText, tone: "bg-gray-100 text-gray-500" };
  const AIcon = ic.Icon;
  return (
    <tr className="hover:bg-gray-50">
      <td className="px-3 py-2.5 text-xs text-gray-400">{number}</td>
      <td className="px-3 py-2.5 text-xs text-gray-600 whitespace-nowrap">{formatDate(a.timestamp)}</td>
      <td className="px-3 py-2.5 whitespace-nowrap">
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-800">
          <span className={`w-5 h-5 rounded-full flex items-center justify-center ${ic.tone}`}>
            <AIcon size={11} />
          </span>
          {AUDIT_LABELS[a.action] || a.action}
        </span>
      </td>
      <td className="px-3 py-2.5 text-xs text-gray-600 max-w-[220px] truncate">{a.detail || "—"}</td>
      <td className="px-3 py-2.5 text-xs text-gray-700 whitespace-nowrap">{a.actor === "system" ? "System" : a.actor}</td>
      <td className="px-3 py-2.5 text-xs text-gray-500 whitespace-nowrap">{a.source}</td>
      <td className="px-3 py-2.5">
        <span className="px-2 py-0.5 rounded-md text-[10px] font-medium bg-green-50 text-green-600">Success</span>
      </td>
    </tr>
  );
}

function AuditTable({ rows }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[640px]">
        <thead>
          <tr className="border-y border-gray-100 bg-gray-50/60">
            {["#", "Timestamp", "Action", "Details", "User", "Source", "Status"].map((h) => (
              <th
                key={h}
                className="text-left px-3 py-2.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((a, i) => (
            <AuditRow key={a._key} a={a} number={i + 1} />
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={7} className="px-3 py-8 text-center text-xs text-gray-400">
                No events match these filters.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function AuditPanel({ view }) {
  const { req, audit, actionFilter, setActionFilter, userFilter, setUserFilter, exportAudit } = view;
  return (
    <div className="border border-gray-200 rounded-xl bg-white p-4">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h3 className="text-sm font-semibold text-gray-900">Audit Trail</h3>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <select value={actionFilter} onChange={(e) => setActionFilter(e.target.value)} className={FILTER_SELECT_CLASS}>
              <option>All Actions</option>
              {audit.actions.map((a) => (
                <option key={a} value={a}>
                  {AUDIT_LABELS[a] || a}
                </option>
              ))}
            </select>
            <Filter size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          </div>

          <div className="relative">
            <select value={userFilter} onChange={(e) => setUserFilter(e.target.value)} className={FILTER_SELECT_CLASS}>
              <option>All Users</option>
              {audit.users.map((u) => (
                <option key={u} value={u}>
                  {u === "system" ? "System" : u}
                </option>
              ))}
            </select>
            <User size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          </div>

          <button
            onClick={exportAudit}
            className="h-8 px-3 border border-gray-200 rounded-lg text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-1.5"
          >
            <Download size={12} />
            Export
          </button>
        </div>
      </div>

      <AuditTable rows={audit.rows} />

      <p className="text-[11px] text-gray-400 mt-3">
        Showing {audit.rows.length} of {req.audit.length} entries
      </p>
    </div>
  );
}

const TAB_PANELS = {
  "Request Details": RequestDetailsPanel,
  "Document Preview": PreviewPanel,
  "Verification Details": VerificationPanel,
  "Audit Trail": AuditPanel,
};

/* ---------- footer actions ---------- */

const OUTLINE_BTN =
  "h-8 px-3 bg-white hover:bg-indigo-50 border border-indigo-400 text-indigo-600 disabled:opacity-60 rounded-lg text-xs font-medium flex items-center gap-1.5 transition";

function FooterActions({ req, verb, busy, inProgress, actions, onOpenModal }) {
  if (req.canAct) {
    return (
      <>
        <button onClick={() => onOpenModal("info")} disabled={busy} className={OUTLINE_BTN}>
          <MessageSquare size={14} />
          Request More Information
        </button>
        <button
          onClick={() => onOpenModal("reject")}
          disabled={busy}
          className="h-8 px-3 bg-indigo-50 hover:bg-indigo-100 disabled:opacity-60 text-indigo-700 border border-indigo-200 rounded-lg text-xs font-medium flex items-center gap-1.5 transition"
        >
          <X size={14} />
          Reject
        </button>
        <button
          onClick={() => actions.primaryAction(verb, req.document)}
          disabled={busy}
          className="h-8 px-4 bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white rounded-lg text-xs font-medium flex items-center gap-1.5 transition"
        >
          <Check size={14} />
          {verb === "Sign" ? "Sign Document" : "Approve & Anchor"}
        </button>
      </>
    );
  }

  if (req.isSender && inProgress) {
    return (
      <>
        <button onClick={actions.remind} disabled={busy} className={OUTLINE_BTN}>
          <Send size={14} />
          Send Reminder
        </button>
        <button
          onClick={actions.cancelRequest}
          disabled={busy}
          className="h-8 px-3 bg-white hover:bg-red-50 border border-red-300 text-red-500 disabled:opacity-60 rounded-lg text-xs font-medium flex items-center gap-1.5 transition"
        >
          <XCircle size={14} />
          Cancel Request
        </button>
      </>
    );
  }

  const idle = inProgress
    ? "Nothing to do right now. You'll be notified when it's your turn."
    : "This request is closed, so no further action is needed.";
  return <p className="text-xs text-gray-500">{idle}</p>;
}

/* ---------- sidebars (right column) ---------- */

function DocLabel({ req }) {
  return (
    <>
      <div className="w-9 h-10 rounded bg-red-500 flex items-center justify-center shrink-0">
        <FileText size={16} className="text-white" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-gray-800 truncate">{show(req.document)}</p>
        <p className="text-[11px] text-gray-500">{show(req.size)}</p>
      </div>
    </>
  );
}

function StatusBanner({ banner, showLabel }) {
  const tone = BANNER_TONES[banner.tone];
  const ToneIcon = tone.Icon;
  return (
    <div className={`rounded-lg border px-3 py-2.5 flex items-center gap-2.5 ${tone.box}`}>
      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${tone.icon}`}>
        <ToneIcon size={20} />
      </div>
      <div>
        {showLabel && <p className="text-[11px] text-gray-500">Current Status</p>}
        <p className="text-sm font-semibold text-gray-900">{banner.title}</p>
        {!showLabel && <p className="text-[11px] text-gray-500">{banner.text}</p>}
      </div>
    </div>
  );
}

function VerificationSidebar({ view }) {
  const { req, verificationBlocks, setTab } = view;
  return (
    <>
      <SidebarCard title="Document Preview">
        <div className="flex items-center gap-3">
          <DocLabel req={req} />
          <button
            onClick={() => setTab("Document Preview")}
            className="h-8 px-3 border border-gray-200 rounded-lg text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-1.5 shrink-0"
          >
            <Eye size={13} />
            View
          </button>
        </div>
      </SidebarCard>

      <SidebarCard title="Verification Summary">
        <ul className="space-y-2">
          {verificationBlocks.map((b) => (
            <li key={b.key} className="flex items-center justify-between gap-2 text-xs">
              <span className="text-gray-700">{b.title}</span>
              <span className={`font-medium shrink-0 ${CHECK_TEXT_COLOR[b.state] || "text-gray-400"}`}>
                {b.state === "Pending" ? "Not Available" : b.state}
              </span>
            </li>
          ))}
        </ul>
      </SidebarCard>

      <SidebarCard title="Additional Information">
        <AdditionalInfo req={req} />
      </SidebarCard>

      <SidebarCard title="Timeline">
        <EventTimeline events={req.audit.slice().reverse()} />
      </SidebarCard>
    </>
  );
}

const QUICK_ACTION_CLASS =
  "w-full h-8 border border-gray-200 rounded-lg text-xs text-gray-700 hover:bg-gray-50 flex items-center justify-center gap-1.5";

function AuditSidebar({ view }) {
  const { req, banner, setTab, exportAudit } = view;
  return (
    <>
      <StatusBanner banner={banner} showLabel />

      <SidebarCard title="Request Timeline">
        <EventTimeline events={req.audit.slice().reverse()} />
      </SidebarCard>

      <SidebarCard title="Quick Actions">
        <div className="space-y-2">
          <button onClick={() => setTab("Document Preview")} className={QUICK_ACTION_CLASS}>
            <Eye size={13} />
            View Document
          </button>
          <button onClick={exportAudit} className={QUICK_ACTION_CLASS}>
            <Download size={13} />
            Download Audit Report
          </button>
          {req.explorerUrl && (
            <a href={req.explorerUrl} target="_blank" rel="noreferrer" className={QUICK_ACTION_CLASS}>
              <ExternalLink size={13} />
              View on Blockchain
            </a>
          )}
        </div>
      </SidebarCard>

      <SidebarCard title="Related Information">
        <AdditionalInfo req={req} />
      </SidebarCard>
    </>
  );
}

function DetailsSidebar({ view }) {
  const { req, banner, fileSrc, setTab } = view;
  return (
    <>
      <StatusBanner banner={banner} />

      <SidebarCard title="Document Summary">
        <div className="flex items-center gap-3">
          <DocLabel req={req} />
        </div>
        <div className="grid grid-cols-2 gap-2 mt-3">
          <button
            onClick={() => setTab("Document Preview")}
            className="h-8 border border-gray-200 rounded-lg text-xs text-gray-700 hover:bg-gray-50 flex items-center justify-center gap-1.5"
          >
            <Eye size={13} />
            View File
          </button>
          <a
            href={`${fileSrc}&download=1`}
            className="h-8 border border-gray-200 rounded-lg text-xs text-gray-700 hover:bg-gray-50 flex items-center justify-center gap-1.5"
          >
            <Download size={13} />
            Download
          </a>
        </div>
      </SidebarCard>

      <SidebarCard title="Security Verification">
        <ul className="space-y-2">
          {req.checks.map((c) => (
            <li key={c.label} className="flex items-center justify-between gap-2 text-xs">
              <span className="flex items-center gap-2 text-gray-700">
                <CheckCircle2 size={15} className={CHECK_ICON_COLOR[c.state] || "text-gray-300"} />
                {c.label}
              </span>
              <span className={`font-medium shrink-0 ${CHECK_TEXT_COLOR[c.state]}`}>{c.state}</span>
            </li>
          ))}
        </ul>
      </SidebarCard>
    </>
  );
}

const TAB_SIDEBARS = {
  "Verification Details": VerificationSidebar,
  "Audit Trail": AuditSidebar,
};

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export default function RequestDetailsPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const email = useUserEmail();

  const { req, loading, error, load } = useRequest(id, email);
  const actions = useRequestActions({ id, email, load, navigate });

  const [tab, setTab] = useState("Request Details");
  const [actionFilter, setActionFilter] = useState("All Actions");
  const [userFilter, setUserFilter] = useState("All Users");
  // which version the preview shows; "signed" means the latest (or the original if unsigned)
  const [previewVersion, setPreviewVersion] = useState("signed");

  const goBack = () => navigate("/request-status");

  if (loading) {
    return <div className="p-10 text-center text-sm text-gray-400">Loading request...</div>;
  }

  if (error || !req) {
    return (
      <div className="p-10 text-center">
        <p className="text-sm text-gray-500 mb-4">{error || "Request not found."}</p>
        <button
          onClick={goBack}
          className="h-9 px-4 border border-indigo-300 text-indigo-600 rounded-lg text-sm hover:bg-indigo-50"
        >
          Back to Requests
        </button>
      </div>
    );
  }

  const verb = req.myRole === "Approver" ? "Approve" : "Sign";
  const verificationBlocks = buildVerificationBlocks(req);
  const audit = buildAuditView(req.audit, actionFilter, userFilter);

  const view = {
    req,
    email,
    tab,
    setTab,
    banner: buildBanner(req, verb),
    fileSrc: buildFileSrc(req, email, previewVersion),
    isSigned: !!req.signed,
    signatures: req.signatures || [],
    previewVersion,
    setPreviewVersion,
    verificationBlocks,
    overallValid: isOverallValid(verificationBlocks),
    audit,
    actionFilter,
    setActionFilter,
    userFilter,
    setUserFilter,
    exportAudit: () => downloadCSV(`${req.id}-audit-trail.csv`, toCSV(audit.rows)),
  };

  const Panel = TAB_PANELS[tab];
  const Sidebar = TAB_SIDEBARS[tab] || DetailsSidebar;
  const inProgress = req.requestStatus === "in_progress";

  return (
    <div style={{ fontFamily: "'Inter', 'DM Sans', system-ui, sans-serif" }} className="min-w-0">
      <PageIntro req={req} verb={verb} onBack={goBack} />
      <FlashMessage flash={actions.flash} />

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_290px] gap-3 items-start">
        {/* ============ LEFT COLUMN ============ */}
        <div className="min-w-0 space-y-3">
          <HeaderCard req={req} tab={tab} onTab={setTab} />
          <Panel view={view} />

          {/* ---------- FOOTER ACTION BAR (all tabs) ---------- */}
          <div className="border-t border-gray-100 pt-4 flex items-center justify-end gap-2.5 flex-wrap">
            <FooterActions
              req={req}
              verb={verb}
              busy={actions.busy}
              inProgress={inProgress}
              actions={actions}
              onOpenModal={actions.setModal}
            />
          </div>
        </div>

        {/* ============ RIGHT COLUMN (content depends on the active tab) ============ */}
        <div className="space-y-3 min-w-0">
          <Sidebar view={view} />
        </div>
      </div>

      {/* MODALS */}
      {actions.modal === "reject" && (
        <ActionModal
          title="Reject request"
          placeholder="Reason for rejection..."
          confirmLabel="Reject request"
          danger
          busy={actions.busy}
          onCancel={() => actions.setModal(null)}
          onConfirm={actions.reject}
        />
      )}
      {actions.modal === "info" && (
        <ActionModal
          title="Request more information"
          placeholder="What do you need to know from the requester?"
          confirmLabel="Send question"
          busy={actions.busy}
          onCancel={() => actions.setModal(null)}
          onConfirm={actions.requestInfo}
        />
      )}
    </div>
  );
}